import React from 'react';

import { BetManager, Bus, constants, prepareRequest } from '@atlas/core';
import { BetOutcomeDefaultCard, Button, Icon, Label } from '@atlas/ui';

import BetOutcomeLink from 'common/BetOutcomeLink';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

// Preorder request
const createPreOrderBet = async bets => {
  const request = prepareRequest({
    method: 'POST',
    url: 'ordering/public/bet-booking/pre-order:create',
  });

  return request({ bets });
};

const initialState = {
  error: null,
  loading: false,
  result: null,
};

const reducer = (action, state) => ({ ...action, ...state });
export const BetslipPreOrderReducer = () => React.useReducer(reducer, initialState);

export const BetslipPreOrderCreateBtn = props => {
  const { amount, preOrderDispatch, preOrderState, selectedBet } = props;

  const handleClick = React.useCallback(() => {
    const bet = BetManager.betProcessor.processBet(selectedBet);

    // Setting bet amount
    bet.betMoney.betAmount = amount;

    // Reset
    preOrderDispatch({
      error: null,
      loading: true,
      result: null,
    });

    createPreOrderBet([bet])
      .then(result => {
        BetManager.clearBets();

        preOrderDispatch({
          loading: false,
          result,
        });

        Bus.emit(Bus.events.bet.bookSuccess, result);
      })
      .catch(error => {
        preOrderDispatch({
          error,
          loading: false,
        });

        Bus.emit(Bus.events.bet.bookError, error);
      });
  }, [amount, selectedBet, preOrderDispatch]);

  let dataTestId;

  if (preOrderState.loading) {
    dataTestId = 'betslip-loading';
  }

  if (selectedBet.active) {
    dataTestId = 'betslip-place-bet';
  }

  return (
    <div className="bottom-button-container bottom-button-container_with_padding">
      <Button
        className="btn_full_width"
        dataTestId={dataTestId}
        disabled={!selectedBet.active || preOrderState.loading}
        label="betslip_pre_order"
        loading={preOrderState.loading}
        onClick={handleClick}
        size="big"
        type="secondary"
      />

      {preOrderState.error && (
        <div className="help-text-container message error">
          <Label message={`preorder_error_${preOrderState.error.code}`} />
        </div>
      )}
    </div>
  );
};

export function BetslipPreOrderReport(props) {
  const { bets, preOrderDispatch, preorderId } = props;

  const handleCloseReport = React.useCallback(
    () => preOrderDispatch({ result: null }),
    [preOrderDispatch],
  );

  return bets.map(bet => (
    <BetslipPreOrderReportItem
      betMoney={bet.betMoney}
      betSize={bet.betSize}
      betStatus={bet.status}
      betType={bet.betType}
      handleCloseReport={handleCloseReport}
      key={bet.requestId}
      outcomes={bet.outcomes}
      preorderId={preorderId}
    />
  ));
}

const BetslipPreOrderReportItem = props => {
  const { betSize, betStatus, betType, handleCloseReport, outcomes, preorderId } = props;

  const isSystemBetType = betType === constants.BET_TYPE.SYSTEM;

  const barcode = React.useRef(null);

  React.useEffect(() => {
    if (barcode.current) {
      import('jsbarcode').then(JsBarcodeModule => {
        JsBarcodeModule.default(barcode.current, preorderId, {
          displayValue: false,
          format: 'CODE128',
          height: 60,
          width: 2,
        });
      });
    }
  }, []);

  return (
    <div className="betslip-preorder-report betslip-bet-report betslip-bet-report_without_border">
      <div className="betslip-bet-report__header">
        <Icon className="betslip-bet-report__success" name="success" />
        <Label
          className="betslip-bet-report__title"
          dataTestId="betslip-bet-report-success"
          message="pre_order_bet_placed"
        />
      </div>

      {betType && (
        <div className="bet-order-header">
          <span className="bet-order-header__type">
            <Label message={betType} />
            {isSystemBetType && betSize}
          </span>
        </div>
      )}

      <div className="bet-items">
        <div>
          {outcomes.map(outcome => (
            <BetOutcomeDefaultCard
              BetOutcomeLink={BetOutcomeLink}
              betStatus={betStatus}
              betType={betType}
              hiddenStatus
              key={outcome.outcomeId}
              outcome={outcome}
            />
          ))}
        </div>
      </div>

      <div className="betslip-bet-report__content">
        <Label className="betslip-bet-report__label" message="preorder_id" />

        <div className="betslip-bet-report__value">{preorderId}</div>

        <svg id="barcode" ref={barcode} />
      </div>

      <div className="betslip-bet-report__footer">
        <button className="btn primary big block" onClick={handleCloseReport} type="button">
          <Label dataTestId="betslip-bet-report-ok" message="ok" />
        </button>
      </div>
    </div>
  );
};
